import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Layers,
  Users,
  MapPin,
  Building2,
  Calendar,
  Map as MapIcon,
  ArrowRight,
} from 'lucide-react';

import { StatCard } from '@/components/primitives/StatCard';
import { Section } from '@/components/primitives/Section';
import { cn } from '@/lib/utils';

// Legacy JS api modules — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as boundariesApi from '@/api/boundaries';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as parcelsApi from '@/api/parcels';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as fpaMembersApi from '@/api/fpaMembers';

import type { FPATenant } from '../FPASettingsHub';

interface OverviewTabProps {
  tenantId: number;
  tenant: FPATenant | null;
  onGoToTab: (next: string) => void;
}

interface OverviewState {
  boundaries: number | null;
  members: number | null;
  lastReindex: string | null;
  primaryName: string | null;
  loading: boolean;
}

const INITIAL: OverviewState = {
  boundaries: null,
  members: null,
  lastReindex: null,
  primaryName: null,
  loading: true,
};

/**
 * The Overview tab is the FPA admin's at-a-glance home. Four stat cards
 * + a row of quick-action shortcuts to the other tabs. Each API call is
 * fired independently and failures are soft — a failing endpoint shows
 * a "—" in its card rather than blocking the whole page.
 */
export default function OverviewTab({
  tenantId,
  tenant,
  onGoToTab,
}: OverviewTabProps) {
  const [state, setState] = useState<OverviewState>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL);

    const tasks: Promise<void>[] = [
      boundariesApi
        .listBoundaries({ tenant_id: tenantId, page_size: 200 })
        .then(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data: any) => {
            if (cancelled) return;
            const list = (data?.boundaries ?? []) as Array<{
              name?: string;
              is_primary?: boolean;
            }>;
            const primary = list.find((b) => b.is_primary);
            setState((s) => ({
              ...s,
              boundaries: list.length,
              primaryName: primary?.name ?? null,
            }));
          },
        )
        .catch(() => {
          if (!cancelled) setState((s) => ({ ...s, boundaries: 0 }));
        }),

      fpaMembersApi
        .getMembersCacheStatus(tenantId)
        .then(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data: any) => {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              members: typeof data?.member_count === 'number' ? data.member_count : 0,
            }));
          },
        )
        .catch(() => {
          if (!cancelled) setState((s) => ({ ...s, members: 0 }));
        }),

      parcelsApi
        .getParcelCacheStatus()
        .then(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (data: any) => {
            if (cancelled) return;
            setState((s) => ({
              ...s,
              lastReindex: data?.last_reindex_at ?? null,
            }));
          },
        )
        .catch(() => {
          if (!cancelled) setState((s) => ({ ...s, lastReindex: null }));
        }),
    ];

    Promise.allSettled(tasks).finally(() => {
      if (!cancelled) setState((s) => ({ ...s, loading: false }));
    });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const lastReindexHint = state.lastReindex
    ? formatRelative(state.lastReindex)
    : 'No reindex yet';

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Layers}
          tone="primary"
          label="Boundaries"
          value={state.boundaries == null ? '—' : state.boundaries}
          hint={state.primaryName ? `Primary: ${state.primaryName}` : 'No primary set'}
          loading={state.loading && state.boundaries == null}
        />
        <StatCard
          icon={Users}
          tone="secondary"
          label="Members"
          value={state.members == null ? '—' : state.members}
          hint="Across this FPA"
          loading={state.loading && state.members == null}
        />
        <StatCard
          icon={MapPin}
          label="Map centre"
          value={
            tenant?.latitude != null && tenant?.longitude != null
              ? `${Number(tenant.latitude).toFixed(3)}, ${Number(
                  tenant.longitude,
                ).toFixed(3)}`
              : 'Not set'
          }
          hint={tenant?.area ?? 'No area'}
        />
        <StatCard
          icon={Calendar}
          label="Last reindex"
          value={lastReindexHint}
          hint={state.lastReindex ? state.lastReindex.slice(0, 10) : '—'}
          loading={state.loading && state.lastReindex == null}
        />
      </div>

      <Section
        title="Quick actions"
        description="Jump straight to the workflow you need."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ShortcutCard
            icon={Building2}
            title="Edit FPA profile"
            description="Name, area, contact info, map centre."
            onClick={() => onGoToTab('profile')}
          />
          <ShortcutCard
            icon={Layers}
            title="Draw a boundary"
            description="Add or edit FPA boundaries with live parcel preview."
            onClick={() => onGoToTab('boundaries')}
          />
          <ShortcutCard
            icon={Users}
            title="Manage members"
            description="Add, link to parcels, edit hazards."
            onClick={() => onGoToTab('members-parcels')}
          />
          <ShortcutCard
            icon={MapIcon}
            title="Parcels map"
            description="See linked parcels coloured by member."
            href={`/admin/tenants/${tenantId}/parcels-map`}
          />
        </div>
      </Section>
    </div>
  );
}

interface ShortcutCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  onClick?: () => void;
  href?: string;
  className?: string;
}

function ShortcutCard({
  icon: Icon,
  title,
  description,
  onClick,
  href,
  className,
}: ShortcutCardProps) {
  const body = (
    <>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="font-medium leading-tight">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </>
  );
  const classes = cn(
    'flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/60',
    className,
  );
  if (href) {
    return (
      <Link to={href} className={classes}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes}>
      {body}
    </button>
  );
}

/** Rough "X minutes/hours/days ago" — only used in stat hints. */
function formatRelative(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const diffMs = Date.now() - then;
    if (diffMs < 0) return 'Just now';
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso.slice(0, 16);
  }
}

